import ConfirmDialog from '@/components/ConfirmDialog';
import Constants from '@/Constants';
import Category from '@/models/Category';
import { CourseLessonContentTO } from '@/models/CourseLessonContentTO';
import { Tag } from '@/models/Item';
import { analyticsService, PlaylistEvents } from '@/services/AnalyticsService';
import { ApplicationState } from '@/store';
import { closeModal } from '@/store/common/actions';
import {
    archiveListRequest,
    clearArchiveConfiguration,
    clearUpdateConfiguration,
    createCourseReset,
    getCourseRequest,
    updateCourseRequest,
} from '@/store/list/action';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import MyListForm from '../MyListForm';
import './style.scss';

const EditCoursePage: React.FC = () => {
    const history = useHistory();
    const dispatch = useDispatch();

    const { courseId } = useParams<{ courseId: string }>();
    const [showDeleteModal, setDeleteModal] = useState<boolean>(false);
    const [isFormDirty, setIsFormDirty] = useState<boolean>(false);

    const {
        strings,
        organizationId,
        author,
        ownerId,
        myCourse,
        successOnArchiveList,
        loading,
        hasErrorOnUpdate,
        hasErrorOnArchive,
        successOnUpdateList,
    } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        organizationId: state.identity.data.user && state.identity.data.user.orgId,
        author: `${state.identity.data.person.firstName} ${state.identity.data.person.lastName}`,
        ownerId: state.identity.data.person.id,
        myCourse: state.lists.courseData,
        successOnArchiveList: state.lists.successOnArchiveList,
        loading: state.lists.loading,
        successOnUpdateList: state.lists.successOnUpdateList,
        hasErrorOnUpdate: state.lists.hasErrorOnUpdate,
        hasErrorOnArchive: state.lists.hasErrorOnArchive,
    }));

    const draftCourse: CourseLessonContentTO = {
        name: '',
        description: '',
        locale: strings._LANGUAGE,
        applicationId: Constants.APP_ID,
        organizationId,
        moduleId: Constants.PLAYLIST_MODULE_ID,
        author,
        ownerId,
        categories: [] as Category[],
        tags: [] as Tag[],
        type: 'REGULAR',
    } as CourseLessonContentTO;

    const [course, setCourse] = useState<CourseLessonContentTO>(draftCourse);
    const [isFormValid, setIsFormValid] = useState<boolean>(true);

    const handleUpdateList = (event: SyntheticEvent) => {
        event.preventDefault();
        dispatch(closeModal(Constants.modal.MORE_OPTIONS));
        dispatch(updateCourseRequest(course));
    };

    const deleteListHandler = (event: SyntheticEvent) => {
        event.preventDefault();
        setDeleteModal(true);
    };

    const onConfirmListDeletion = () => {
        dispatch(archiveListRequest(Number(courseId), false, false));
        setDeleteModal(false);
    };

    const onTryAgainHandler = () => {
        if (hasErrorOnArchive && !loading) {
            dispatch(archiveListRequest(Number(courseId), false, false));
        } else {
            dispatch(updateCourseRequest(course));
        }
    };

    useEffect(() => {
        dispatch(getCourseRequest(Number(courseId)));

        return () => {
            dispatch(clearUpdateConfiguration());
            dispatch(clearArchiveConfiguration());
            dispatch(createCourseReset());
        };
    }, [courseId]);

    useEffect(() => {
        if (myCourse.id) {
            if ((myCourse.id === Number(courseId) && myCourse.status === 'ARCHIVED') || myCourse.ownerId !== ownerId) {
                history.replace('/mine/mycreations');
            }
        }
    }, [myCourse?.status, myCourse?.id]);

    useEffect(() => {
        if (successOnArchiveList) {
            setDeleteModal(false);
            analyticsService.logEvent(PlaylistEvents.DELETE_STUDENT_PLAYLIST, {
                entityId: course.id,
                entityName: course.name,
            });
            history.replace('/mine/mycreations');
        }
    }, [successOnArchiveList]);

    useEffect(() => {
        if (successOnUpdateList) {
            history.goBack();
            history.replace(`/courses/${courseId}`);
        }
    }, [successOnUpdateList]);

    return (
        <>
            <ConfirmDialog
                content=""
                icon={Constants.icons.REPORT_PROBLEM}
                leftButtonTitle={strings.NO}
                leftClick={() => setDeleteModal(false)}
                rightButtonTitle={strings.YES}
                rightClick={onConfirmListDeletion}
                show={showDeleteModal}
                title={strings.CONFIRM_MYLIST_DELETION}
            />

            <ConfirmDialog
                content={strings.UPDATE_MYLIST_ERROR}
                icon={Constants.icons.REPORT_PROBLEM}
                rightButtonTitle={strings.TRY_AGAIN}
                rightClick={onTryAgainHandler}
                show={(hasErrorOnArchive || hasErrorOnUpdate) && !loading}
                title={strings.OPS}
            />

            <div className="mylist">
                <div className="edit-mylist">
                    {myCourse && (
                        <MyListForm
                            course={myCourse}
                            setCourse={setCourse}
                            setIsFormDirty={setIsFormDirty}
                            setIsFormValid={setIsFormValid}
                            title={strings.EDIT_LIST}
                        ></MyListForm>
                    )}

                    <div className="button-container">
                        <button
                            className={`btn btn-edit ${isFormValid && isFormDirty ? '' : 'disabled'}`}
                            disabled={!isFormValid || !isFormDirty}
                            onClick={(e) => handleUpdateList(e)}
                            type="button"
                        >
                            {strings.SAVE}
                        </button>
                        <button className="btn btn-delete" onClick={(e) => deleteListHandler(e)} type="button">
                            {strings.DELETE}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};
export default EditCoursePage;
